import { motion, useInView } from "framer-motion";
import React, { useRef, useState } from "react";
import { FaArrowRight, FaDownload, FaDollarSign, FaUsers, FaRocket, FaClock, FaMobile, FaShieldAlt, FaGlobe, FaBrain, FaCode } from "react-icons/fa";
import { STANDARD_VARIANTS, HOVER_ANIMATIONS } from "../constants/animations";
import { BUTTON_STYLES, CONTAINER_STYLES, TYPOGRAPHY_STYLES, CARD_STYLES } from "../constants/styles";

interface CaseStudy {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
    icon: React.ComponentType<any>;
  }[];
  technologies: string[];
  image: string;
  category: "mobile" | "ai" | "android" | "web";
  timeline: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: 1,
    title: "Always On AMOLED",
    subtitle: "Mobile App with 20M+ Downloads",
    description: "Revolutionary always-on display solution for Android that achieved global recognition and successful acquisition.",
    challenge: "Create an energy-efficient always-on display app that works across diverse Android devices while maintaining optimal battery performance.",
    solution: "Developed sophisticated power management algorithms, device-specific optimizations, and leveraged undocumented Android APIs to create an intuitive Material Design interface that scales across all Android versions.",
    results: [
      { metric: "Downloads", value: "20M+", icon: FaDownload },
      { metric: "Global Reach", value: "180+ countries", icon: FaGlobe },
      { metric: "Outcome", value: "Successful Exit", icon: FaDollarSign },
    ],
    technologies: ["Android", "Material Design", "Low-Level", "Battery Optimization"],
    image: "alwaysonamoled.webp",
    category: "android",
    timeline: "8 years",
  },
  {
    id: 2,
    title: "Mixtiles AI Backend",
    subtitle: "Production AI Infrastructure Serving Millions",
    description: "Built scalable AI backend powering next-generation photo and video experiences for millions of Mixtiles users.",
    challenge: "Architect production-grade AI infrastructure that processes photos and videos at scale while intelligently understanding user galleries and preferences.",
    solution: "Designed and implemented a robust Python backend with AWS CDK infrastructure, pioneering AI heuristics to detect trips, faces, and identify users' most important people. Built predictive algorithms in uncharted territory to anticipate user needs before they ask, integrating cutting-edge AI models for photo enhancement, timelapse generation, and artistic filters.",
    results: [
      { metric: "Users Served", value: "Millions", icon: FaUsers },
      { metric: "AI Innovation", value: "Predictive UX", icon: FaBrain },
      { metric: "Infrastructure", value: "High Scale", icon: FaRocket },
    ],
    technologies: ["AI/ML", "DevOps", "Data Science", "Production Systems"],
    image: "mixtiles.png",
    category: "web",
    timeline: "Current",
  },
  {
    id: 3,
    title: "Hadasha News AI",
    subtitle: "Mindful AI-Powered Mobile News App",
    description: "Agenda-free news delivery using AI to synthesize consensus-based reporting from multiple Hebrew sources.",
    challenge: "Combat information overload and media bias by creating an AI system that identifies consensus across multiple news sources while respecting user attention.",
    solution: "Built a sophisticated AI pipeline that processes daily news articles, identifies common topics, and synthesizes them into coherent, bias-free stories focusing only on factual consensus.",
    results: [
      { metric: "Mindful UI", value: "Psychology", icon: FaBrain },
      { metric: "Innovation", value: "Bias-free AI", icon: FaShieldAlt },
      { metric: "Platforms", value: "iOS & Android", icon: FaMobile },
    ],
    technologies: ["AI/ML", "NLP", "Python", "AWS", "Flutter", "UI/UX Design"],
    image: "hadasha.webp",
    category: "mobile",
    timeline: "4 months",
  },
  {
    id: 4,
    title: "Mobile MCP Framework",
    subtitle: "Pioneering AI-Mobile Integration",
    description: "First-of-its-kind framework enabling seamless integration between locally-running LLMs and mobile applications.",
    challenge: "Bridge the gap between powerful AI models and mobile app capabilities on constrained mobile platforms without desktop-style subprocess support.",
    solution: "Developed an innovative two-sided framework using Android AIDL and MCP protocol adaptation, allowing local LLMs to discover and interact with any app's capabilities through standardized annotations.",
    results: [
      { metric: "Innovation", value: "First-of-kind", icon: FaRocket },
      { metric: "Development", value: "3 weeks", icon: FaClock },
      { metric: "Technical Depth", value: "AIDL + MCP", icon: FaCode },
    ],
    technologies: ["Android", "Kotlin", "MCP", "Open Source", "Mobile LLMs"],
    image: "mobilemcp.webp",
    category: "ai",
    timeline: "3 weeks",
  },
];

const CaseStudyCard: React.FC<{ study: CaseStudy; index: number }> = ({ study, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categoryColors = {
    android: "from-teal-800 to-emerald-900",
    ai: "from-yellow-800 to-yellow-900",
    mobile: "from-orange-800 to-red-900",
    web: "from-indigo-700 to-violet-900",
  };

  return (
    <motion.div
      variants={STANDARD_VARIANTS.itemMedium}
      className={`${CARD_STYLES.interactive} overflow-hidden hover:shadow-2xl transition-all duration-500`}
    >
      <div className={`h-2 bg-gradient-to-r ${categoryColors[study.category]}`} />

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <span className="inline-block bg-surface-100 dark:bg-accent-700 text-accent-600 dark:text-accent-300 text-sm px-3 py-1 rounded-full mb-2 capitalize">
              {study.category}
            </span>
            <h3 className="text-2xl font-bold text-accent-900 dark:text-surface-50 mb-2">
              {study.title}
            </h3>
            <p className="text-primary-600 dark:text-primary-400 font-semibold mb-3">
              {study.subtitle}
            </p>
          </div>
          <div className="text-right text-sm text-accent-500 dark:text-accent-400">
            <FaClock className="inline mr-1" />
            {study.timeline}
          </div>
        </div>

        <p className="text-accent-700 dark:text-accent-300 mb-6 leading-relaxed">
          {study.description}
        </p>

        <div className="grid grid-cols-3 gap-4 mb-6">
          {study.results.map((result, idx) => (
            <div key={idx} className="text-center">
              <div className="flex justify-center mb-2">
                <result.icon className="text-primary-500 text-xl" />
              </div>
              <div className="font-bold text-lg text-accent-900 dark:text-surface-50">
                {result.value}
              </div>
              <div className="text-sm text-accent-600 dark:text-accent-400">
                {result.metric}
              </div>
            </div>
          ))}
        </div>

        <motion.div
          initial={false}
          animate={{ height: isExpanded ? "auto" : 0, opacity: isExpanded ? 1 : 0 }}
          className="overflow-hidden"
        >
          <div className="border-t border-surface-200 dark:border-accent-700 pt-6 mb-6">
            <div className="mb-4">
              <h4 className="font-semibold text-accent-900 dark:text-surface-50 mb-2">
                Challenge
              </h4>
              <p className="text-accent-700 dark:text-accent-300 text-sm">
                {study.challenge}
              </p>
            </div>
            <div className="mb-4">
              <h4 className="font-semibold text-accent-900 dark:text-surface-50 mb-2">
                Solution
              </h4>
              <p className="text-accent-700 dark:text-accent-300 text-sm">
                {study.solution}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-6">
          {study.technologies.map((tech, idx) => (
            <span
              key={idx}
              className="bg-surface-100 dark:bg-accent-700 text-accent-700 dark:text-accent-300 text-xs px-3 py-1 rounded-full"
            >
              {tech}
            </span>
          ))}
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors duration-200"
        >
          {isExpanded ? "Show Less" : "View Details"}
          <FaArrowRight className={`ml-2 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
        </button>
      </div>
    </motion.div>
  );
};

const CaseStudies: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <motion.section
      ref={ref}
      className={CONTAINER_STYLES.section}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={STANDARD_VARIANTS.container}
    >
      <div className={CONTAINER_STYLES.contentWide}>
        <motion.div variants={STANDARD_VARIANTS.item} className="text-center mb-16">
          <h2 className={TYPOGRAPHY_STYLES.sectionTitle}>
            Case Studies
          </h2>
          <p className={`${TYPOGRAPHY_STYLES.sectionSubtitle} max-w-3xl mx-auto`}>
            Detailed breakdowns of how I've solved complex technical challenges and delivered measurable business results.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {caseStudies.map((study, index) => (
            <CaseStudyCard key={study.id} study={study} index={index} />
          ))}
        </div>

        <motion.div
          variants={STANDARD_VARIANTS.item}
          className="text-center mt-16"
        >
          <div className="bg-surface-200 dark:bg-accent-800 rounded-xl p-8 border border-surface-300 dark:border-accent-700">
            <h3 className="text-2xl font-bold text-accent-900 dark:text-surface-50 mb-4">
              Ready to Create Your Success Story?
            </h3>
            <p className="text-accent-600 dark:text-accent-400 mb-6">
              Let's discuss how I can help you achieve similar results with your next project.
            </p>
            <motion.button
              {...HOVER_ANIMATIONS.scale}
              className={BUTTON_STYLES.primary}
              onClick={() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Discuss Your Project
            </motion.button>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default CaseStudies;